import React, { useEffect, useState } from 'react';
import { confirmAlert } from 'react-confirm-alert';
import { ManufacturersProvider } from 'providers';
import { useForm } from 'react-hook-form';
import { Container } from 'reactstrap';
import {
    Breadcrumb,
    Devices,
    FeatherIcon,
} from 'components';
import { Layout } from 'containers';
import Swal from 'sweetalert2';

const Manufacturers = () => {
    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
    } = useForm();
    const [pageLoading, setPageLoading] = useState(true);
    const [manufacturer, setManufacturer] = useState(null);
    const [manufacturers, setManufacturers] = useState([]);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showUpdateModal, setShowUpdateModal] = useState(false);
    const breadcrumb = [
        { title: 'Devices', link: '/devices', active: false },
        { title: 'Manufacturers', active: true },
    ];

    const actionButtons = [{
        type: 'button',
        title: 'Add Manufacturer',
        classes: 'btn btn-sm pd-x-15 btn-primary btn-uppercase mg-l-5',
        icon: <FeatherIcon icon="plus" classes="wd-10 mg-r-5" />,
        action: () => setShowAddModal(true),
    }];

    async function fetchData() {
        if (!pageLoading) setPageLoading(true);
        const manufacturers = await ManufacturersProvider.getAll();
        setManufacturers(manufacturers.results || []);
        setPageLoading(false);
    };

    useEffect(() => {
        fetchData();
    }, []);

    async function handleDelete(id, name) {
        return confirmAlert({
            title: 'Confirm Delete',
            message: `Are you sure you want to delete ${name}? This action cannot be undone, and will fail if there are any devices attached to ${name}.`,
            buttons: [
                {
                    label: 'Yes, Continue',
                    onClick: async () => {
                        setPageLoading(true);
                        const result = await ManufacturersProvider.delete(id);

                        if (result.status === 'success') {
                            setPageLoading(false);
                            Swal.fire({
                                position: 'center',
                                icon: 'success',
                                title: `${name} successfully deleted!`,
                                showConfirmButton: false,
                                timer: 5000
                            });
                            return setTimeout(async function () {
                                fetchData();
                            }, 5000);
                        }

                        // error occured
                        setPageLoading(false);
                        return Swal.fire({
                            position: 'center',
                            icon: 'error',
                            title: result.message || 'Failed to process request, please try again!',
                            showConfirmButton: false,
                            timer: 5000
                        });
                    },
                },
                {
                    label: 'Cancel',
                },
            ],
        });
    }

    async function handleUpdate(id, data) {
        setPageLoading(true);
        const result = await ManufacturersProvider.update(id, data);
        const { status, message } = result;

        if (status === 'success') {
            setPageLoading(false);
            Swal.fire({
                position: 'center',
                icon: 'success',
                title: `${data.name || 'Manufacturer'} successfully updated!`,
                showConfirmButton: false,
                timer: 5000
            });
            return setTimeout(async function () {
                fetchData();
            }, 5000);
        }

        // error occured
        setPageLoading(false);
        return Swal.fire({
            position: 'center',
            icon: 'error',
            title: message || 'Failed to process request, please try again!',
            showConfirmButton: false,
            timer: 5000
        });
    }

    async function onUpdate(data) {
        const { name } = data;
        return handleUpdate(manufacturer.id, { name });
    }

    async function onCreate(data) {
        setPageLoading(true);
        const result = await ManufacturersProvider.create(data);
        const { status, message } = result;

        if (status === 'success') {
            setPageLoading(false);
            Swal.fire({
                position: 'center',
                icon: 'success',
                title: `${data.name} successfully created!`,
                showConfirmButton: false,
                timer: 5000
            });
            return setTimeout(async function () {
                fetchData();
            }, 5000);
        }

        // error occured
        setPageLoading(false);
        return Swal.fire({
            position: 'center',
            icon: 'error',
            title: message || 'Failed to process request, please try again!',
            showConfirmButton: false,
            timer: 5000
        });
    }

    return (
        <Layout pageLoading={pageLoading}>
            <Devices.UpdateManufacturer
                reset={reset}
                errors={errors}
                register={register}
                onUpdate={onUpdate}
                show={showUpdateModal}
                manufacturer={manufacturer}
                handleSubmit={handleSubmit}
                setShow={setShowUpdateModal}
                setManufacturer={setManufacturer}
            />
            <Devices.AddManufacturer
                reset={reset}
                errors={errors}
                register={register}
                onCreate={onCreate}
                show={showAddModal}
                setShow={setShowAddModal}
                handleSubmit={handleSubmit}
            />
            <div className="content content-fixed pd-b-0 manufacturers">
                <Container fluid className="pd-x-0 pd-lg-x-10 pd-xl-x-0">
                    <Breadcrumb
                        heading="Manage Manufacturers"
                        actions={actionButtons}
                        items={breadcrumb}
                    />
                </Container>
            </div>
            <Devices.ManufacturerList
                data={manufacturers}
                handleUpdate={handleUpdate}
                handleDelete={handleDelete}
                setManufacturer={setManufacturer}
                setShowUpdateModal={setShowUpdateModal}
            />
        </Layout>
    );
};

export default Manufacturers;
